import type { PayComponentOption } from '@/hooks/hr';

/**
 * Plain checks and shapes for the Employees screen's forms, kept apart from
 * the components so they can be tested on their own.
 */

export const CNIC_RE = /^\d{5}-\d{7}-\d$/;
export const IBAN_RE = /^PK\d{2}[A-Z]{4}[0-9A-Z]{16}$/;
export const WALLET_RE = /^03\d{9}$/;

/** Types a CNIC's dashes in for you: 3520212345671 → 35202-1234567-1. */
export function formatCnic(input: string): string {
  const d = input.replace(/\D/g, '').slice(0, 13);
  if (d.length <= 5) return d;
  if (d.length <= 12) return `${d.slice(0, 5)}-${d.slice(5)}`;
  return `${d.slice(0, 5)}-${d.slice(5, 12)}-${d.slice(12)}`;
}

/** IBAN as the bank prints it, but without spaces and in capitals. */
export function cleanIban(input: string): string {
  return input.replace(/\s/g, '').toUpperCase();
}

export function isMoney(v: string): boolean {
  return /^\d+(\.\d{1,2})?$/.test(v.trim());
}

/** Earnings that can be part of someone's set pay: Basic and the company's
 *  own allowances. Overtime, arrears and the like are worked out by payroll. */
export function structureComponents(all: PayComponentOption[] | undefined): PayComponentOption[] {
  return (all ?? [])
    .filter((c) => c.kind === 'EARNING' && (c.systemCode === null || c.systemCode === 'BASIC'))
    .sort((a, b) => (a.systemCode === 'BASIC' ? -1 : b.systemCode === 'BASIC' ? 1 : a.sortOrder - b.sortOrder));
}

/** Allowances and deductions that can be fixed monthly items. */
export function fixedItemComponents(all: PayComponentOption[] | undefined): PayComponentOption[] {
  return (all ?? []).filter((c) => c.systemCode === null && c.kind !== 'EMPLOYER');
}

export type PayAmounts = Record<string, string>;

/** Sum of the entered amounts, ignoring anything that is not a number. */
export function payTotal(amounts: PayAmounts): number {
  return Object.values(amounts).reduce((t, v) => t + (isMoney(v) ? Number(v) : 0), 0);
}

/** Checks a structure or daily rate; returns a plain-English problem or null. */
export function payProblem(
  workerType: 'MONTHLY' | 'DAILY',
  components: PayComponentOption[],
  amounts: PayAmounts,
  dailyRate: string,
): string | null {
  if (workerType === 'DAILY') {
    if (!dailyRate.trim()) return 'Enter the daily rate.';
    if (!isMoney(dailyRate) || Number(dailyRate) <= 0) return 'The daily rate must be a number above zero.';
    return null;
  }
  const basic = components.find((c) => c.systemCode === 'BASIC');
  if (!basic) return 'Basic pay is not set up yet. Add it in HR Setup first.';
  const b = amounts[basic.id] ?? '';
  if (!b.trim()) return 'Enter the basic pay.';
  if (!isMoney(b) || Number(b) <= 0) return 'Basic pay must be a number above zero.';
  for (const c of components) {
    const v = amounts[c.id] ?? '';
    if (v.trim() && !isMoney(v)) return `${c.nameEn} must be a number, like 5000.`;
  }
  return null;
}

/** What the API takes as `pay`. */
export function payInput(workerType: 'MONTHLY' | 'DAILY', amounts: PayAmounts, dailyRate: string) {
  if (workerType === 'DAILY') return { dailyRate: dailyRate.trim() };
  return {
    lines: Object.entries(amounts)
      .filter(([, v]) => isMoney(v) && Number(v) > 0)
      .map(([componentId, amount]) => ({ componentId, amount: amount.trim() })),
  };
}
